<script setup lang="ts">
  import { notify } from 'notiwind'
  import { Form } from 'vee-validate'
  import * as yup from 'yup'
  import { handleError } from '@/utils/error'
  import type { ApplyAccountPayload, ApplyProfile } from '~/types/apply'

  const props = withDefaults(
    defineProps<{
      profile?: ApplyProfile | null
      submitting?: boolean
      resumeDeposit?: boolean // /depot-cv : le flow crée le compte lui-même
    }>(),
    { profile: null, submitting: false, resumeDeposit: false }
  )

  const emit = defineEmits<{
    submit: [payload?: ApplyAccountPayload]
    back: []
  }>()

  const { fetchUser, user } = useUser()
  const auth = useStrapiAuth()

  const tab = ref<'create' | 'login'>('create')
  const loggingIn = ref(false)

  const loginSchema = yup.object({
    identifier: yup.string().required('${label} requis').email('${label} invalide').label('Email'),
    password: yup.string().required('${label} requis').label('Mot de passe')
  })

  const onLogin = async (values: { identifier: string; password: string }) => {
    if (loggingIn.value) return
    loggingIn.value = true
    try {
      await auth.login(values)
      await fetchUser()
      if (!(user.value as { candidate?: { id?: number } } | null)?.candidate?.id) {
        notify({
          group: 'main',
          type: 'danger',
          title: 'Compte non candidat',
          text: 'Ce compte n’a pas de profil candidat associé. Veuillez utiliser un compte candidat ou créer un nouveau compte.'
        })
        return
      }
      emit('submit')
    } catch (err) {
      const error = err.error || err
      handleError(error, error.message === 'Invalid identifier or password' ? 'Email ou mot de passe incorrect' : true)
    } finally {
      loggingIn.value = false
    }
  }

  const creating = ref(false)

  const createSchema = yup.object({
    email: yup.string().required('${label} requis').email('${label} invalide').label('Email'),
    password: yup
      .string()
      .required('${label} requis')
      .min(12, 'Le mot de passe doit contenir au moins ${min} caractères')
      .matches(/[a-z]/, 'Le mot de passe doit contenir au moins une lettre minuscule')
      .matches(/[A-Z]/, 'Le mot de passe doit contenir au moins une lettre majuscule')
      .matches(/\d/, 'Le mot de passe doit contenir au moins un chiffre')
      .matches(/[!"#$%&'()*+,\-./:;<=>?@[\]_`{}~]/, 'Le mot de passe doit contenir au moins un caractère spécial')
      .label('Mot de passe')
  })

  const onCreate = async (values: { email: string; password: string; newsletter?: boolean }) => {
    if (creating.value) return

    // The flow sends account, candidate and CV data in one registration request.
    if (props.resumeDeposit) {
      emit('submit', { newAccount: true, email: values.email, password: values.password, newsletter: !!values.newsletter })
      return
    }

    creating.value = true
    try {
      const p: Partial<ApplyProfile> = props.profile || {}
      await auth.register({
        gender: p.gender,
        firstName: p.firstName,
        lastName: p.lastName,
        email: values.email,
        phone: p.phone,
        password: values.password,
        newsletter: !!values.newsletter,
        cgu: true,
        username: values.email,
        networks: { linkedin: p.linkedin },
        candidateData: { gender: p.gender, firstName: p.firstName, lastName: p.lastName, email: values.email, phone: p.phone }
      })
      notify({
        group: 'main',
        type: 'info',
        title: 'E-mail de confirmation envoyé',
        text: `Un e-mail de confirmation a été envoyé à l’adresse ${values.email}.`
      })
      emit('submit', { newAccount: true, email: values.email })
    } catch (err) {
      const error = err.error || err
      handleError(error, error.message === 'Email or Username are already taken' ? 'Cet email a déjà un compte - connectez-vous.' : true)
    } finally {
      creating.value = false
    }
  }
</script>

<template>
  <div class="mx-auto w-full max-w-lg">
    <h1 class="text-center font-special text-3xl font-bold">Mon compte</h1>

    <!-- Already authenticated -->
    <div class="mt-4 space-y-4" v-if="user">
      <p class="text-center text-zinc-600">
        Connecté·e en tant que <span class="font-bold">{{ user.email }}</span>
      </p>
      <div class="flex items-center justify-center gap-3">
        <Button color="secondary" outline type="button" @click="emit('back')">Retour</Button>
        <Button :disabled="submitting" type="button" @click="emit('submit')">{{
          resumeDeposit ? 'Déposer mon CV' : 'Envoyer ma candidature'
        }}</Button>
      </div>
    </div>

    <!-- Not authenticated: create an account or log in -->
    <div class="mt-4" v-else>
      <div class="mb-4 flex justify-center gap-6 border-b">
        <button
          class="-mb-px border-b-2 px-2 pb-2"
          type="button"
          :class="tab === 'create' ? 'border-viking-400 font-bold' : 'border-transparent text-zinc-400'"
          @click="tab = 'create'"
        >
          Créer un compte
        </button>
        <button
          class="-mb-px border-b-2 px-2 pb-2"
          type="button"
          :class="tab === 'login' ? 'border-viking-400 font-bold' : 'border-transparent text-zinc-400'"
          @click="tab = 'login'"
        >
          J'ai déjà un compte
        </button>
      </div>

      <!-- Create an account: email pre-filled from step 2 + password (with the rules) + newsletter -->
      <Form
        class="mt-2"
        v-if="tab === 'create'"
        v-slot="{ errors }"
        :initial-values="{ email: profile?.email, newsletter: false }"
        :validation-schema="createSchema"
        @submit="onCreate"
      >
        <div class="-m-2 flex flex-wrap">
          <FormField class="basis-full p-2" autocomplete="email" name="email" type="email" placeholder="Email" :error="errors.email" required />
          <FormField
            class="basis-full p-2"
            autocomplete="new-password"
            name="password"
            type="password"
            placeholder="Mot de passe"
            :error="errors.password"
            required
          />
          <span class="flex space-x-4 px-4 pb-1 text-sm text-zinc-500">
            <ul>
              <li>- 12 caractère minimum</li>
              <li>- 1 majuscule</li>
            </ul>
            <ul>
              <li>- 1 chiffre</li>
              <li>- 1 caractère spécial</li>
            </ul>
          </span>
          <FormField
            class="basis-full p-2 text-sm"
            id="newsletter"
            label="J’accepte d’être contacté(e) par Profil Public (offre d’emploi, newsletter…)"
            name="newsletter"
            type="checkbox"
            :unchecked-value="false"
            :value="true"
          />
        </div>
        <div class="mt-4 flex items-center justify-center gap-3">
          <Button color="secondary" outline type="button" @click="emit('back')">Retour</Button>
          <Button :disabled="creating || submitting" type="submit">{{
            resumeDeposit ? 'Déposer mon CV et créer mon compte' : 'Postuler et créer mon compte'
          }}</Button>
        </div>
      </Form>

      <!-- Log into an existing account, then submit — same look as the #connexion modal -->
      <Form class="mt-2" v-else v-slot="{ errors }" :validation-schema="loginSchema" @submit="onLogin">
        <div class="-m-2 flex flex-wrap">
          <FormField
            class="basis-full p-2"
            autocomplete="email"
            name="identifier"
            type="email"
            placeholder="Email"
            :error="errors.identifier"
            required
          />
          <FormField
            class="basis-full p-2"
            autocomplete="current-password"
            name="password"
            type="password"
            placeholder="Mot de passe"
            :error="errors.password"
            required
          />
        </div>
        <div class="mt-2 text-right">
          <NuxtLink class="text-sm text-zinc-500 hover:text-viking-500" replace to="#motDePasseOublie">Mot de passe oublié&nbsp;?</NuxtLink>
        </div>
        <div class="mt-4 flex items-center justify-center gap-3">
          <Button color="secondary" outline type="button" @click="emit('back')">Retour</Button>
          <Button :disabled="loggingIn || submitting" type="submit">{{
            resumeDeposit ? 'Se connecter et déposer mon CV' : 'Se connecter et postuler'
          }}</Button>
        </div>
      </Form>
    </div>
  </div>
</template>
